/**
 * Onglet Documents - Profil Unifie (PL + AC)
 * 
 * FRONT-08: Interface unifiee pour Prestataire Libre ET Accord Cadre
 * FRONT-21: Refonte affichage documents par chapitres thématiques
 * Utilise ProfileContext pour les donnees.
 * 
 * NOTE: Le logo est gere separement via /api/profile/logo (stocke dans storage, pas en DB)
 * 
 * @see CORE-11-upload-logo.md pour l'API logo
 * @see CORE-12 pour les documents non modifiables
 * @see FRONT-21-refonte-chapitres-documents.md pour la structure en chapitres
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useProfile } from '../../../contexts/ProfileContext';
import Button from '../../../composants/Button/Button';
import UploadDocumentModal from '../../../composants/UploadDocumentModal/UploadDocumentModal';
import UploadLogoModal from '../../../composants/UploadLogoModal/UploadLogoModal';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/export.svg';
import DocumentsList from '../../../composants/DocumentsList/DocumentsList';
import { DOCUMENT_TYPES } from '../../../services/documentService';
import type { DocumentType } from '../../../services/documentService';
import { useDocumentPermissions } from '../../../hooks/useDocumentPermissions';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import '../../../composants/MesDocuments/MesDocuments.css';
import './TabDocuments.css';

// FRONT-21: Types pour les chapitres de documents
type DocumentChapterType = 'obligatoires' | 'ac_2025_2026' | 'ac_2027_2028';

interface DocumentSubSection {
  type: DocumentType | 'logo';
  label: string;
  note?: string; // Message informatif spécifique
  required?: boolean;
}

interface DocumentChapter {
  id: DocumentChapterType;
  title: string;
  description?: string; // Message en haut du chapitre
  visibleFor: 'all' | 'accord_cadre'; // Qui peut voir ce chapitre
  subSections: DocumentSubSection[];
  readOnly?: boolean; // Si true, tous les docs sont en lecture seule
}

// FRONT-21: Configuration des chapitres thématiques
const DOCUMENT_CHAPTERS: DocumentChapter[] = [
  {
    id: 'obligatoires',
    title: 'Documents obligatoires (à maintenir à jour)',
    visibleFor: 'all',
    subSections: [
      { type: 'logo', label: 'Logo entreprise', required: true },
      { type: DOCUMENT_TYPES.KBIS, label: 'Extrait Kbis', required: true, note: "Ce document n'est modifiable qu'en faisant la demande par écrit auprès de Centralis." },
      { type: DOCUMENT_TYPES.ATTESTATION_FISCALE, label: 'Attestation fiscale', required: true },
      { type: DOCUMENT_TYPES.ATTESTATION_SOCIALE, label: 'Attestation sociale', required: true },
      { type: DOCUMENT_TYPES.RC_PRO, label: 'Assurance responsabilité civile', required: true },
      {
        type: DOCUMENT_TYPES.GARANTIE_DECENNALE,
        label: 'Assurance garantie décennale',
        note: 'Pour les entreprises non soumises, chargez votre RCP.',
        required: true
      },
      { type: DOCUMENT_TYPES.QUALIFICATIONS, label: 'Certifications / qualifications', required: true },
      { type: 'cv_equipe' as DocumentType, label: 'CV de l\'équipe encadrante', required: true },
      { type: DOCUMENT_TYPES.RIB, label: 'RIB', required: true },
      { type: DOCUMENT_TYPES.DECLARATION_HONNEUR, label: 'Déclaration sur l\'honneur', required: true },
    ],
  },
  {
    id: 'ac_2025_2026',
    title: 'Accord-cadre édition 2025-2026',
    visibleFor: 'all',
    readOnly: true,
    subSections: [
      { type: 'contrat_pdf' as DocumentType, label: 'Contrat (CCAP + AE)' },
      { type: 'cctp' as DocumentType, label: 'CCTP' },
    ],
  },
  // {
  //   id: 'ac_2027_2028',
  //   title: 'Accord-cadre édition 2027-2028',
  //   description: 'Documents à venir pour l\'\u00e9dition 2027-2028',
  //   visibleFor: 'all',
  //   readOnly: true,
  //   subSections: [], // Vide pour l'instant (futur)
  // },
];

// FRONT-08: Interface pour les contrats Centralis (AC uniquement)
interface ContratCentralis {
  id: number;
  numero: string;
  lot_technique?: {
    id: number;
    code: string;
    nom: string;
  } | null;
  departements?: Array<{
    id: number;
    nom: string;
    region_nom?: string | null;
  }>;
  documents: Array<{
    nom: string;
    type: string;
    binaire?: string;
  }>;
  updated_at?: string;
}

// Info pour la modale des zones géographiques
interface LotZonesModalData {
  lotCode: string;
  lotNom: string;
  isRegional: boolean;
  zones: Array<{ id: number; nom: string }>;
}

// FRONT-21: Le composant détecte automatiquement le rôle pour afficher les chapitres appropriés
const TabDocuments: React.FC = () => {
  const { documents, uploadDocument, isSaving, profile, loadProfile } = useProfile();
  const { checkPermission } = useDocumentPermissions();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<DocumentType | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [logoPreviewOpen, setLogoPreviewOpen] = useState(false);
  const [isLogoDragOver, setIsLogoDragOver] = useState(false);

  // Tooltip fixe (passe au-dessus de tout)
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  const handleSectionMouseEnter = (text: string, e: React.MouseEvent<HTMLElement>) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({ text, x: rect.left + rect.width / 2, y: rect.top });
  };

  const handleSectionMouseLeave = () => setTooltip(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isDeletingLogo, setIsDeletingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Modale zones géographiques des lots AC
  const [lotZonesModal, setLotZonesModal] = useState<LotZonesModalData | null>(null);

  // Bloquer le scroll quand une modale est ouverte
  useEffect(() => {
    const anyOpen = uploadModalOpen || logoModalOpen || logoPreviewOpen || !!lotZonesModal;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [uploadModalOpen, logoModalOpen, logoPreviewOpen, lotZonesModal]);

  // FRONT-08: Etat pour les contrats Centralis (AC)
  const [contrats, setContrats] = useState<ContratCentralis[]>([]);
  const [isLoadingContrats, setIsLoadingContrats] = useState(false);

  // FRONT-21: Etat pour les documents communs (CCTP)
  const [documentsCommuns, setDocumentsCommuns] = useState<any[]>([]);
  const [isLoadingDocumentsCommuns, setIsLoadingDocumentsCommuns] = useState(false);

  // FRONT-21: Déterminer si l'utilisateur est titulaire d'un AC (a des contrats)
  const hasContinueContracts = useMemo(() => {
    const hasContracts = contrats.length > 0;
    console.log('hasContinueContracts:', hasContracts, '- Nombre de contrats:', contrats.length);
    return hasContracts;
  }, [contrats]);

  // FRONT-21: Filtrer les chapitres visibles selon le profil
  const visibleChapters = useMemo(() => {
    const filtered = DOCUMENT_CHAPTERS.filter(chapter => {
      if (chapter.visibleFor === 'all' && chapter.id === 'obligatoires') return true;
      // Chapitres AC visibles uniquement si l'utilisateur a des contrats
      if (chapter.id === 'ac_2025_2026' || chapter.id === 'ac_2027_2028') {
        return hasContinueContracts;
      }
      return false;
    });
    console.log('Chapitres visibles:', filtered.map(c => c.id));
    return filtered;
  }, [hasContinueContracts]);

  // FRONT-21: Charger les contrats pour détecter les titulaires AC
  useEffect(() => {
    loadContratsCentralis();
    loadDocumentsCommuns();
  }, []);

  const loadContratsCentralis = async () => {
    setIsLoadingContrats(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      // FRONT-08: Utiliser la route existante pour les contrats AC
      const response = await fetch(`${apiUrl}/documents/my-entite-contrats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📜 Contrats Centralis - Réponse API:', data);
        const loadedContrats = data.data?.contrats || [];
        console.log('📜 Contrats Centralis - Nombre chargés:', loadedContrats.length);
        setContrats(loadedContrats);
      } else {
        console.error('Erreur API contrats:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Erreur chargement contrats Centralis:', error);
    } finally {
      setIsLoadingContrats(false);
    }
  };

  const loadDocumentsCommuns = async () => {
    setIsLoadingDocumentsCommuns(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiUrl}/documents-communs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Documents communs - Réponse API:', data);
        // Gérer différentes structures de réponse
        // Laravel paginate retourne: { status: 'success', data: { data: [...], current_page, ... } }
        let docs = [];
        if (Array.isArray(data)) {
          docs = data;
        } else if (data.data) {
          // Si data.data est un objet de pagination, extraire data.data.data
          if (data.data.data && Array.isArray(data.data.data)) {
            docs = data.data.data;
          } else if (Array.isArray(data.data)) {
            docs = data.data;
          }
        }
        console.log('Documents communs - Après parsing:', docs);
        // La bibliographie acheteur est réservée à l'onglet Profil > Documents acheteur
        setDocumentsCommuns(docs.filter((d: any) => d.type !== 'bibliographie_acheteur'));
      } else {
        console.error('Erreur API documents-communs:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Erreur chargement documents communs:', error);
      setDocumentsCommuns([]);
    } finally {
      setIsLoadingDocumentsCommuns(false);
    }
  };

  // Grouper les documents par type et trier par date (plus recent en premier)
  const documentsByType = useMemo(() => {
    const grouped: Record<string, typeof documents> = {};
    documents.forEach(doc => {
      if (!grouped[doc.type]) {
        grouped[doc.type] = [];
      }
      grouped[doc.type].push(doc);
    });

    // FRONT-06: Trier chaque groupe par date decroissante (plus recent en premier)
    Object.keys(grouped).forEach(type => {
      grouped[type].sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at).getTime();
        const dateB = new Date(b.updated_at || b.created_at).getTime();
        return dateB - dateA; // Decroissant
      });
    });

    return grouped;
  }, [documents]);

  // Ouvrir modal d'upload
  const handleFileUpload = (type: DocumentType, label: string) => {
    setSelectedDocType(type);
    setSelectedLabel(label);
    setUploadModalOpen(true);
  };

  // Upload du document
  const handleUploadDocument = async (file: File, type: string, commentaire?: string) => {
    if (!selectedDocType) return;
    await uploadDocument(file, selectedDocType, commentaire);
    setUploadModalOpen(false);
    setSelectedDocType(null);
    setSelectedLabel('');
  };

  // Upload du logo (route spécifique /api/profile/logo)
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation côté client
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Format non accepté. Utilisez JPG, PNG, GIF ou WebP.');
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2 Mo
    if (file.size > maxSize) {
      setLogoError('Le fichier est trop volumineux (max 2 Mo).');
      return;
    }

    setLogoError(null);
    setIsUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiUrl}/profile/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de l\'upload du logo');
      }

      // Recharger le profil pour avoir le nouveau logo
      await loadProfile();
    } catch (error: any) {
      console.error('Erreur upload logo:', error);
      setLogoError(error.message || 'Erreur lors de l\'upload du logo');
    } finally {
      setIsUploadingLogo(false);
      // Reset input pour permettre de re-sélectionner le même fichier
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  // Supprimer le logo (route DELETE /api/profile/logo)
  const handleDeleteLogo = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer le logo ?')) {
      return;
    }

    setLogoError(null);
    setIsDeletingLogo(true);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiUrl}/profile/logo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la suppression du logo');
      }

      // Recharger le profil
      await loadProfile();
    } catch (error: any) {
      console.error('Erreur suppression logo:', error);
      setLogoError(error.message || 'Erreur lors de la suppression du logo');
    } finally {
      setIsDeletingLogo(false);
    }
  };

  // FRONT-21: Rendu d'un chapitre
  const renderChapter = (chapter: DocumentChapter) => {
    // Si chapitre futur (pas de subSections), afficher message
    if (chapter.subSections.length === 0) {
      return (
        <SectionPliable key={chapter.id} title={chapter.title}>
          {chapter.description && (
            <div className="chapter-description">{chapter.description}</div>
          )}
          <div className="chapter-content">
            <p className="info-text" style={{ fontStyle: 'italic' }}>
              Les documents pour cette édition seront disponibles lors de votre
              souscription à l'accord-cadre 2027-2028.
            </p>
          </div>
        </SectionPliable>
      );
    }

    return (
      <SectionPliable key={chapter.id} title={chapter.title}>
        {chapter.description && (
          <div className="chapter-description">{chapter.description}</div>
        )}
        <div className={`chapter-content${chapter.subSections.length > 0 ? ' chapter-content--grid4' : ''}`}>
          {chapter.subSections.map(subSection =>
            renderSubSection(subSection, chapter.readOnly)
          )}
        </div>
      </SectionPliable>
    );
  };

  // FRONT-21: Rendu d'un sous-chapitre (section document)
  const renderSubSection = (
    subSection: DocumentSubSection,
    chapterReadOnly: boolean = false
  ) => {
    // Cas spécial : Logo
    if (subSection.type === 'logo') {
      return renderLogoSection(subSection);
    }

    // Cas spécial : Documents AC (contrat_pdf, cctp)
    if (['contrat_pdf', 'cctp'].includes(subSection.type as string)) {
      return renderContratSection(subSection);
    }

    // Documents standards - afficher uniquement le dernier uploadé
    const docs = (documentsByType[subSection.type] || []).slice(0, 1);
    const hasDocuments = docs.length > 0;
    const permission = checkPermission(subSection.type, hasDocuments);
    const canUpload = !chapterReadOnly && permission.canUpload;

    const formattedDocs = docs.map(doc => ({
      id: doc.id,
      type: doc.type_label || subSection.label,
      commentaire: doc.commentaire || undefined,
      updated_at: doc.updated_at || doc.created_at,
      key: doc.key,
      category: 'entreprise' as const,
      filename: doc.filename,
    }));

    const tooltipText = subSection.note
      || (hasDocuments && !canUpload && permission.message ? permission.message : null);

    return (
      <section
        key={subSection.type}
        className="document-section"
        onMouseEnter={tooltipText ? (e) => handleSectionMouseEnter(tooltipText, e) : undefined}
        onMouseLeave={tooltipText ? handleSectionMouseLeave : undefined}
      >
        <div className={`section-content${!hasDocuments ? ' section-content--empty' : ''}`} style={{ width: '100%', boxSizing: 'border-box' }}>
          <h2 className="subsection-header" style={{ textAlign: 'center', width: '100%' }}>
            {subSection.label}{subSection.required && <span className="required-asterisk">*</span>}
          </h2>
          {(hasDocuments || canUpload) ? (
            <DocumentsList
              documents={formattedDocs}
              variant="inline"
              iconColor={canUpload ? 'green' : 'grey'}
              hideLabel
              onAdd={canUpload ? () => handleFileUpload(subSection.type, subSection.label) : undefined}
              onAddDisabled={isSaving}
              addCardError={!hasDocuments && !!subSection.required}
              onDrop={canUpload ? async (file) => { const ok = await uploadDocument(file, subSection.type as DocumentType); if (!ok) throw new Error('Erreur lors de l\'upload'); } : undefined}
              onModify={canUpload ? () => handleFileUpload(subSection.type, subSection.label) : undefined}
            />
          ) : (
            <span className="info-text" style={{ padding: 0, fontStyle: 'italic' }}>&nbsp;Aucun document</span>
          )}
        </div>
      </section>
    );
  };

  // FRONT-21: Rendu de la section Contrat Centralis (AC)
  const renderContratSection = (subSection: DocumentSubSection) => {
    const documentTypeKey = subSection.type as string;

    // CCTP : deux cellules fixes — Travaux et Ingénierie
    if (documentTypeKey === 'cctp') {
      const cctpIcon = (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      );

      const cctpVariants: { key: string; label: string; keyword: string }[] = [
        { key: 'cctp-travaux', label: 'CCTP Travaux', keyword: 'travaux' },
        { key: 'cctp-ingenierie', label: 'CCTP Ingénierie', keyword: 'ingenierie' },
      ];

      return (
        <>
          {cctpVariants.map(({ key, label, keyword }) => {
            const docs = Array.isArray(documentsCommuns)
              ? documentsCommuns.filter(doc => {
                const normalized = doc.type?.toLowerCase()
                  .replace(/[-\s]/g, '')
                  .normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
                return normalized.includes('cctp') && normalized.includes(keyword);
              })
              : [];

            const formattedDocs = docs.map(doc => ({
              id: doc.id,
              type: doc.type || label,
              commentaire: doc.commentaire,
              updated_at: doc.updated_at,
              category: 'commun' as const,
              filename: doc.nom || `${label}.pdf`,
            }));

            return (
              <section key={key} className="document-section">
                <div className="section-content">
                  <h2 className="subsection-header" style={{ textAlign: 'center', width: '100%' }}>
                    {label}
                  </h2>
                  {isLoadingDocumentsCommuns ? (
                    <div className="loading-text">Chargement...</div>
                  ) : (
                    <DocumentsList
                      documents={formattedDocs}
                      variant="inline"
                      iconColor="grey"
                      icon={cctpIcon}
                      hideLabel
                    />
                  )}
                </div>
              </section>
            );
          })}
        </>
      );
    }

    // Pour les contrats, grouper par lot technique
    return (
      <>
        {isLoadingContrats ? (
          <section key="contrats-loading" className="document-section document-section--full-width">
            <div className="section-content">
              <div className="loading-text">Chargement des contrats...</div>
            </div>
          </section>
        ) : contrats.length > 0 ? (
          contrats.flatMap(contrat => {
            const lotCode = contrat.lot_technique?.code || '';
            const contractDoc = contrat.documents?.find(d => d.type === 'Contrat');
            if (!contractDoc?.binaire) return [];
            const titre = `CCAP + AE (lot ${lotCode}) : contrat N°${contrat.numero || contrat.id}`;
            const doc = {
              id: contrat.id,
              type: titre,
              commentaire: '',
              updated_at: contrat.updated_at || new Date().toISOString(),
              category: 'accord_cadre' as const,
              filename: contractDoc.nom || `Contrat_${contrat.id}.pdf`,
              binaire: contractDoc.binaire,
            };
            return [(
              <section key={`contrat-${contrat.id}`} className="document-section">
                <div className="section-content">
                  <h2 className="subsection-header" style={{ textAlign: 'center', width: '100%' }}>
                    {titre}
                  </h2>
                  <DocumentsList
                    documents={[doc]}
                    variant="inline"
                    iconColor="grey"
                    icon={
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                      </svg>
                    }
                    hideLabel
                  />
                </div>
              </section>
            )];
          })
        ) : null}
      </>
    );
  };

  // Rendu de la section Logo (stocké dans storage, pas en DB)
  const LOGO_ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  const handleLogoDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsLogoDragOver(false);
    const file = Array.from(e.dataTransfer.files).find(f => LOGO_ACCEPTED_TYPES.includes(f.type));
    if (!file) { setLogoError('Format non accepté. Utilisez JPG, PNG, GIF ou WebP.'); return; }
    if (file.size > 2 * 1024 * 1024) { setLogoError('Le fichier est trop volumineux (max 2 Mo).'); return; }
    try {
      await handleLogoUploadFromModal(file);
    } catch { /* error already set in handleLogoUploadFromModal */ }
  };

  const handleLogoUploadFromModal = async (file: File) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) throw new Error('Format non accepté.');
    if (file.size > 2 * 1024 * 1024) throw new Error('Le fichier est trop volumineux (max 2 Mo).');
    setLogoError(null);
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/profile/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Erreur lors de l'upload");
      }
      await loadProfile();
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const renderLogoSection = (subSection: DocumentSubSection) => {
    const entite = profile?.entite;
    const hasLogo = !!entite?.logo_path;
    const logoUrl = entite?.logo_url || null;
    const logoDate = (entite as any)?.logo_updated_at || (entite as any)?.updated_at || null;
    const isLoading = isUploadingLogo || isDeletingLogo;

    const formatDate = (iso: string) => {
      const d = new Date(iso);
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
      <section key="logo" className="document-section">

        <div className="section-content">
          <h2 className="subsection-header">
            {subSection.label}
            {subSection.required && <span className="required-asterisk">*</span>}
          </h2>
          <div
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setIsLogoDragOver(true); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsLogoDragOver(false); }}
            onDrop={handleLogoDrop}
            style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}
          >
            {/* Carte logo existant */}
            {hasLogo && logoUrl && (
              <div
                className="document-card logo-card"
                style={{ cursor: 'pointer' }}
                onClick={() => setLogoPreviewOpen(true)}
                title="Prévisualiser"
              >
                <div className="logo-card-thumb">
                  <img
                    src={logoUrl}
                    alt="Logo entreprise"
                    className="logo-card-img"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              </div>
            )}

            {/* Modale preview logo */}
            {logoPreviewOpen && logoUrl && (
              <div className="modal-overlay" onClick={() => setLogoPreviewOpen(false)}>
                <div className="modal-content-document" onClick={(e) => e.stopPropagation()}>
                  <h3>Logo entreprise</h3>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
                    <img
                      src={logoUrl}
                      alt="Logo entreprise"
                      style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px' }}
                    />
                  </div>
                  <div className="modal-actions">
                    <Button
                      onClick={() => { setLogoPreviewOpen(false); setLogoModalOpen(true); }}
                      value="Modifier"
                      size="small"
                      variant="secondary"
                    />
                    <Button
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = logoUrl;
                        a.download = 'logo';
                        a.target = '_blank';
                        a.click();
                      }}
                      icon={<DownloadIcon className="dl-button-icon-white" />}
                      value="Télécharger"
                      size="small"
                    />
                    <Button
                      onClick={() => setLogoPreviewOpen(false)}
                      value="Fermer"
                      size="small"
                      variant="secondary"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Carte "+" → uniquement si pas encore de logo */}
            {!hasLogo && (
              <button
                type="button"
                className={`document-add-card${isLogoDragOver ? ' document-add-card--drag-over' : ''}${subSection.required && !hasLogo ? ' document-add-card--error' : ''}`}
                onClick={() => setLogoModalOpen(true)}
                disabled={isLoading || isSaving}
                title="Ajouter un logo"
              >
                {isLoading ? (
                  <span className="spinner" />
                ) : (
                  <>
                    <span className="document-add-card-plus">+</span>
                    <span className="document-add-card-label">Ajouter</span>
                  </>
                )}
              </button>
            )}
          </div>

          {logoError && (
            <p className="logo-error-text" style={{ marginTop: '0.5rem' }}>{logoError}</p>
          )}
        </div>

        <UploadLogoModal
          isOpen={logoModalOpen}
          onClose={() => setLogoModalOpen(false)}
          onUpload={handleLogoUploadFromModal}
        />
      </section>
    );
  };

  return (
    <div className="mes-documents">
      {/* FRONT-21: Affichage des chapitres selon le profil (PL ou AC) */}
      {visibleChapters.map(chapter => renderChapter(chapter))}

      {/* Tooltip fixe : passe au-dessus de tout */}
      {tooltip && (
        <div
          className="section-note-tooltip--fixed"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Modal d'upload */}
      {uploadModalOpen && selectedDocType && (
        <UploadDocumentModal
          isOpen={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedDocType(null);
            setSelectedLabel('');
          }}
          onUpload={handleUploadDocument}
          preselectedType={selectedLabel}
        />
      )}

      {/* Modale zones géographiques du lot */}
      {lotZonesModal && (
        <div className="modal-overlay" onClick={() => setLotZonesModal(null)}>
          <div className="lot-zones-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="lot-zones-modal-title">
              {lotZonesModal.lotCode} - {lotZonesModal.lotNom}
            </h3>
            <p className="lot-zones-modal-subtitle">
              {lotZonesModal.isRegional ? 'Régions' : 'Départements'} où vous êtes titulaire
            </p>
            <ul className="lot-zones-list">
              {lotZonesModal.zones
                .sort((a, b) => a.nom.localeCompare(b.nom))
                .map(zone => (
                  <li key={`${zone.id}-${zone.nom}`} className="lot-zones-item">
                    {lotZonesModal.isRegional ? zone.nom : `${zone.id} - ${zone.nom}`}
                  </li>
                ))}
            </ul>
            <div className="lot-zones-modal-actions">
              <Button
                value="Fermer"
                variant="secondary"
                onClick={() => setLotZonesModal(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabDocuments;
